import Icon from '@components/common/Icon';

const timelineEvents = [
  {
    year: 'Grade 9',
    title: 'VEX Robotics Journey Begins',
    description: 'Started competing in VEX robotics competitions, learning the fundamentals of robot design, programming, and teamwork.',
    achievements: [
      'First VEX competition experience',
      'Built foundation in autonomous programming',
      'Developed mechanical design skills'
    ]
  },
  {
    year: 'Grade 10',
    title: 'Team Samosa Slappas - National Recognition',
    description: 'Led the Samosa Slappas team to national-level competitions, implementing advanced autonomous systems and earning recognition.',
    achievements: [
      'Philippines National Qualifiers',
      'Design Award recognition',
      'Mentored new team members'
    ]
  },
  {
    year: 'Grade 11',
    title: 'Team Abracadabra - World Stage',
    description: 'Captained Team Abracadabra to VEX World Championship qualification and founded the Philippines\' first FRC team.',
    achievements: [
      'Excellence Award - Philippines Nationals',
      'Qualified for VEX Worlds Taipei 2025',
      'Founded FRC Team Sydney',
      'Rookie Inspiration Award - FRC Sydney Regional'
    ]
  },
  {
    year: '2024-2025',
    title: 'International Competition Circuit',
    description: 'Competed across multiple international competitions, representing the Philippines on the global robotics stage.',
    achievements: [
      'VEX Worlds Taipei 2025',
      'VEX Dallas International Competition',
      'FRC Taipei Regional Finalist',
      'Mentored 30+ students across teams'
    ]
  },
  {
    year: 'Present',
    title: 'Leading Innovation & Mentorship',
    description: 'Currently leading both FRC and VEX teams, focusing on sustainable growth, innovation, and developing the next generation of robotics engineers.',
    achievements: [
      'Team Captain - FRC & VEX',
      'Pioneering FRC growth in Philippines',
      'Building legacy programs',
      'Planning for future competitions'
    ]
  }
];

const ImpactTimelineSection = () => {
  return (
    <section
      className="py-16 sm:py-20 px-4"
      style={{ backgroundColor: 'var(--color-theme-1)' }}
    >
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-16">
          <h2
            className="text-3xl sm:text-4xl font-heading font-bold mb-4 tracking-tight"
            style={{ color: 'var(--color-heading)' }}
          >
            Robotics Journey Timeline
          </h2>
          <p
            className="text-lg max-w-2xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            From beginner to international competitor and team captain
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div
            className="absolute left-8 top-0 bottom-0 w-0.5 hidden md:block"
            style={{ backgroundColor: 'var(--color-border-medium)' }}
          ></div>

          {/* Timeline events */}
          <div className="space-y-12">
            {timelineEvents.map((event, index) => (
              <div key={index} className="relative">
                {/* Timeline dot */}
                <div className="hidden md:flex absolute left-0 items-center justify-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center shadow-card"
                    style={{
                      backgroundColor: 'var(--color-accent-6)',
                      borderWidth: '4px',
                      borderStyle: 'solid',
                      borderColor: 'var(--color-theme-1)'
                    }}
                  >
                    <Icon name="robotics" size="md" className="text-white" />
                  </div>
                </div>

                {/* Content card */}
                <div className="md:ml-28">
                  <div
                    className="rounded-xl p-6 shadow-card hover:shadow-card-hover transition-all duration-200"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: 'var(--color-card-border)',
                      borderWidth: '1px',
                      borderStyle: 'solid'
                    }}
                  >
                    {/* Year badge */}
                    <div
                      className="inline-block px-4 py-1.5 rounded-full text-sm font-bold mb-3"
                      style={{
                        backgroundColor: 'var(--color-accent-1)',
                        color: 'var(--color-accent-7)'
                      }}
                    >
                      {event.year}
                    </div>

                    <h3
                      className="text-2xl font-bold mb-3"
                      style={{ color: 'var(--color-heading)' }}
                    >
                      {event.title}
                    </h3>

                    <p
                      className="text-base leading-relaxed mb-4"
                      style={{ color: 'var(--color-card-text)' }}
                    >
                      {event.description}
                    </p>

                    {/* Achievements */}
                    <div className="space-y-2">
                      {event.achievements.map((achievement, i) => (
                        <div
                          key={i}
                          className="flex items-start text-sm"
                          style={{ color: 'var(--color-muted)' }}
                        >
                          <span
                            className="mr-2 mt-1 flex-shrink-0 font-bold"
                            style={{ color: 'var(--color-accent-6)' }}
                          >
                            ✓
                          </span>
                          {achievement}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Impact Stats */}
        <div
          className="mt-16 rounded-xl p-8 text-center"
          style={{
            backgroundColor: 'var(--color-card-bg)',
            borderColor: 'var(--color-border-light)',
            borderWidth: '1px',
            borderStyle: 'solid'
          }}
        >
          <h3
            className="text-2xl font-bold mb-8"
            style={{ color: 'var(--color-heading)' }}
          >
            Impact & Legacy
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <div
                className="text-4xl font-bold mb-2"
                style={{ color: 'var(--color-accent-7)' }}
              >
                15+
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--color-muted)' }}
              >
                International Competitions
              </div>
            </div>
            <div>
              <div
                className="text-4xl font-bold mb-2"
                style={{ color: 'var(--color-accent-7)' }}
              >
                30+
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--color-muted)' }}
              >
                Students Mentored
              </div>
            </div>
            <div>
              <div
                className="text-4xl font-bold mb-2"
                style={{ color: 'var(--color-accent-7)' }}
              >
                1st
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--color-muted)' }}
              >
                FRC Team in Philippines
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactTimelineSection;
